import { inject, injectable } from "inversify";
import { api, Result } from "src/api/api";
import { API_URL } from "src/constants/api";
import { normalizeError } from "src/services/normalizeError";
import {
  doSetCurrentSpitKit,
  doSetSpitKitBulkOperations,
  doSetSpitKitErrors,
  doSetSpitKitFulfilledList,
  doSetSpitKitImportResult,
  doSetSpitKitList,
  doSetSpitKitListFulfillingNewOrders,
  doSetSpitKitLoading,
  doSetSpitKitPagination,
  doSetSpitKitSummary,
  doSetSpitKitSummaryLoading,
} from "src/store/SpitKit/SpitKitActions";
import { selectSpitKitCurrentOrder } from "src/store/SpitKit/SpitKitSelectors";
import {
  Pagination,
  RGCStatus,
  SpitKitBulkOperation,
  SpitKitExportFileResponse,
  SpitKitExportFilters,
  SpitKitImportDNAFileResult,
  SpitKitImportResult,
  SpitKitListFilters,
  SpitKitOrderEntry,
  SpitKitStatusSummary,
} from "src/store/SpitKit/Types";
import * as IStore from "./types/IStore";
import {
  CreateSpitKitData,
  UpdateSpitKitData,
} from "./types/validations/SpitKitValidations";

export type SpitKitListQuery = {
  filters: SpitKitListFilters;
  pagination: Pagination;
};

export type UpdateOrderCommand = {
  order_id: string;
} & UpdateSpitKitData;

export interface ISpitKitService {
  createSpitKitOrder(orderData: CreateSpitKitData): Promise<void>;
  updateSpitKitOrder(updateCommand: UpdateOrderCommand): Promise<void>;

  loadSpitKit(order_id: string): Promise<void>;

  loadSpitKits(query: SpitKitListQuery): Promise<void>;

  fulfillNewSpitKitOrders(): Promise<void>;

  importSpitKitOrderFulfillment(file: File): Promise<void>;
  importSpitKitDNAFile(file: File): Promise<SpitKitImportDNAFileResult>;
  getUserSpitKits(userId: string): Promise<SpitKitOrderEntry[]>;

  exportSpitKitOrdersFile(
    filters: SpitKitExportFilters,
  ): Promise<SpitKitExportFileResponse | undefined>;

  bulkDeleteSpitKitOrders(orders: SpitKitOrderEntry[]): Promise<void>;
}

type StatusSummaryEntry = {
  status: string;
  amount: number;
};

type PaginationApiResponse = {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
};

type LoadSpitKitsResultData = {
  metadata: StatusSummaryEntry[];
  orders: SpitKitOrderEntry[];
  pagination: PaginationApiResponse;
};

const bulkStatusMessages: Record<keyof SpitKitImportResult, string> = {
  notFound: "Not Found",
  wrongStatus: "Wrong Status",
  success: "Success",
  duplicates: "Duplicates",
};
@injectable()
export class SpitKitService implements ISpitKitService {
  @inject("store")
  private store!: IStore.IStore;

  private dispatchError(error: unknown) {
    const normalizedError = normalizeError(error);
    this.store.dispatch(doSetSpitKitErrors([normalizedError]));
  }

  private async doUpdateStatus(
    order_id: string,
    status: RGCStatus,
  ): Promise<void> {
    await api({
      endpoint: API_URL.ORDERS_UPDATE_STATUS(order_id),
      method: "PUT",
      data: {
        status,
      },
    });
  }

  private async updateStatus(
    order_id: string,
    status: RGCStatus,
  ): Promise<void> {
    try {
      this.doUpdateStatus(order_id, status);
    } catch (error) {
      this.dispatchError(error);
    }
  }

  async exportSpitKitOrdersFile(
    filters: SpitKitExportFilters,
  ): Promise<SpitKitExportFileResponse | undefined> {
    try {
      if ("uuids" in filters) {
        const result = await api<SpitKitExportFileResponse>({
          endpoint: API_URL.ORDERS_EXPORT,
          method: "POST",
          data: { order_uuids: filters.uuids },
        });
        return result.data;
      }

      const data = {
        status: filters.statuses,
        search_term: filters.searchTerm,
        has_tracking_number: filters.hasTrackingNumber,
        no_usps_outbound_since_days: filters.staleOutboundDays,
        no_usps_return_since_days: filters.staleReturnDays,
        excluded_order_uuids: filters.excludedOrderIds,
      };

      const result = await api<SpitKitExportFileResponse>({
        endpoint: API_URL.ORDERS_EXPORT,
        method: "POST",
        data,
      });

      return result.data;
    } catch (error) {
      this.dispatchError(error);
      return undefined;
    }
  }

  async createSpitKitOrder(data: CreateSpitKitData): Promise<void> {
    this.store.dispatch(doSetSpitKitLoading());

    try {
      const result = await api<SpitKitOrderEntry>({
        endpoint: API_URL.ORDERS,
        data,
        method: "POST",
      });

      const incompleteSpitKit = selectSpitKitCurrentOrder(
        this.store.getState(),
      ) as SpitKitOrderEntry;

      this.store.dispatch(
        doSetCurrentSpitKit({
          ...incompleteSpitKit,
          ...result.data,
        }),
      );
    } catch (error) {
      this.dispatchError(error);
    }
  }

  async updateSpitKitOrder({
    order_id,
    ...data
  }: UpdateOrderCommand): Promise<void> {
    this.store.dispatch(doSetSpitKitLoading());

    try {
      await api<SpitKitOrderEntry>({
        endpoint: API_URL.ORDERS_UPDATE(order_id),
        data,
        method: "PUT",
      });
    } catch (error) {
      this.dispatchError(error);
    }
  }

  async loadSpitKit(order_id: string): Promise<void> {
    this.store.dispatch(doSetSpitKitErrors([]));
    this.store.dispatch(doSetSpitKitLoading());

    try {
      const params: Record<string, string> = {
        search_term: String(order_id),
        page_size: String(1),
      };
      const result = await this.doLoadSpitKits(params);
      const { orders } = result.data;

      const entry: SpitKitOrderEntry | undefined = orders.find(
        (order) => order.order_id === order_id,
      );

      if (entry === undefined) {
        throw new Error("Not Found");
      }

      this.store.dispatch(doSetCurrentSpitKit(entry));
    } catch (error) {
      this.dispatchError(error);
    }
  }

  private async doLoadSpitKits(
    params: Record<string, string | number | undefined>,
  ): Promise<Result<LoadSpitKitsResultData>> {
    return await api<LoadSpitKitsResultData>({
      endpoint: API_URL.ORDERS,
      params,
    });
  }

  async getUserSpitKits(userId: string): Promise<SpitKitOrderEntry[]> {
    const params: Record<string, string> = {
      app_user_id: userId,
    };
    const result = await this.doLoadSpitKits(params);
    const { orders } = result.data;
    return orders.filter(({ app_user_id }) => app_user_id === userId);
  }

  async loadSpitKits({ filters, pagination }: SpitKitListQuery): Promise<void> {
    this.store.dispatch(doSetSpitKitLoading());
    this.store.dispatch(doSetSpitKitSummaryLoading());

    const {
      searchTerm,
      status,
      hasTrackingNumber,
      staleOutboundDays,
      staleReturnDays,
      orderBy = "created",
      orderByDirection = "desc",
    } = filters;
    const { page, pageSize } = pagination;

    try {
      const result = await this.doLoadSpitKits({
        page,
        page_size: pageSize,
        order_by: orderBy,
        order_by_direction: orderByDirection,
        search_term: searchTerm,
        status,
        has_tracking_number: hasTrackingNumber ? "true" : undefined,
        no_usps_outbound_since_days: staleOutboundDays,
        no_usps_return_since_days: staleReturnDays,
      });

      const { metadata, orders, pagination } = result.data;

      const spitKitSummary: SpitKitStatusSummary = metadata.reduce(
        ({ received, pending, inFulfillment }, { status, amount }) => ({
          received: status === "ordered" ? amount : received,
          pending: status === "pending" ? amount : pending,
          inFulfillment: status === "in_fulfillment" ? amount : inFulfillment,
        }),
        {
          received: 0,
          pending: 0,
          inFulfillment: 0,
        },
      );

      const paginationData: Pagination = {
        page: pagination.page,
        pageSize: pagination.page_size,
        resultCount: orders.length,
        totalCount: pagination.total_count,
        totalPages: pagination.total_pages,
      };

      this.store.dispatch(doSetSpitKitPagination(paginationData));
      this.store.dispatch(doSetSpitKitList(orders));
      this.store.dispatch(doSetSpitKitSummary(spitKitSummary));
    } catch (error) {
      this.dispatchError(error);
    }
  }

  async fulfillNewSpitKitOrders(): Promise<void> {
    this.store.dispatch(doSetSpitKitListFulfillingNewOrders());

    try {
      const result = await api<SpitKitOrderEntry[]>({
        endpoint: API_URL.ORDERS_EXPORT,
        method: "POST",
      });

      const exportedOrders = result.data;

      this.store.dispatch(doSetSpitKitFulfilledList(exportedOrders));
    } catch (error) {
      this.dispatchError(error);
    }
  }

  async importSpitKitOrderFulfillment(file: File): Promise<void> {
    try {
      const data = new FormData();
      data.append("orders", file);

      const result = await api<SpitKitImportResult>({
        endpoint: API_URL.ORDERS_IMPORT,
        data,
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
        },
      });

      this.store.dispatch(doSetSpitKitImportResult(result.data));
    } catch (error) {
      this.dispatchError(error);
    }
  }

  async importSpitKitDNAFile(file: File): Promise<SpitKitImportDNAFileResult> {
    const data = new FormData();
    data.append("orders", file);

    const result = await api<SpitKitImportDNAFileResult>({
      endpoint: API_URL.ORDERS_DNA_IMPORT,
      data,
      method: "PUT",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return result.data;
  }


  async bulkDeleteSpitKitOrders(orders: SpitKitOrderEntry[]): Promise<void> {
    const operations: SpitKitBulkOperation[] = orders.map<SpitKitBulkOperation>(
      ({ order_id }) => ({ order_id, operationStatus: "running" }),
    );

    this.store.dispatch(doSetSpitKitBulkOperations(operations));

    try {
      const response = await api<SpitKitImportResult>({
        endpoint: API_URL.ORDERS_UPDATE_STATUS_BULK,
        method: "PUT",
        data: {
          status: "deleted",
          order_ids: operations.map(({ order_id }) => order_id),
        },
      });
      const { data } = response;

      const operationsResults: SpitKitBulkOperation[] = Object.entries(
        data,
      ).reduce((accumulate, [resultType, orderIds]) => {
        const statusType = resultType as keyof SpitKitImportResult;
        const succeeded = statusType === "success";
        return [
          ...accumulate,
          ...orderIds.map<SpitKitBulkOperation>((order_id) => ({
            order_id,
            operationStatus: succeeded ? "completed" : "failed",
            reason: succeeded
              ? undefined
              : normalizeError(bulkStatusMessages[statusType]),
          })),
        ];
      }, [] as SpitKitBulkOperation[]);

      this.store.dispatch(doSetSpitKitBulkOperations(operationsResults));
    } catch (error) {
      this.store.dispatch(
        doSetSpitKitBulkOperations(
          operations.map(({ order_id }) => ({
            order_id,
            operationStatus: "failed",
            reason: normalizeError(error),
          })),
        ),
      );
    }
  }
}
